import React, { useEffect } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    Paper,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    useTheme
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Area,
    AreaChart
} from 'recharts';
import { useDashboardController } from './context/dashboard-controller';
import { useDashboardState } from './context/dashboard-context';
import PageHeader from '../../../components/page-header';

export function DashboardComponent() {
    const theme = useTheme();
    const {
        loadData,
        handleYearChange,
        navigateToPurchases,
        navigateToContracts
    } = useDashboardController();

    const {
        dashboardData,
        selectedYear,
        loading
    } = useDashboardState();

    useEffect(() => {
        loadData();
    }, [selectedYear]);

    const getGrowthColor = (percentage: number) => {
        if (percentage > 0) return 'success.main';
        if (percentage < 0) return 'error.main';
        return 'text.secondary';
    };

    const getGrowthIcon = (percentage: number) => {
        if (percentage > 0) return <TrendingUpIcon fontSize="small" />;
        if (percentage < 0) return <TrendingDownIcon fontSize="small" />;
        return null;
    };

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

    const COLORS = [
        theme.palette.primary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
        '#9C27B0',
        '#FF9800',
        '#00BCD4'
    ];

    const purchaseStatusData = [
        { name: 'Em Andamento', value: dashboardData.purchaseStatistics.inProgressPurchases, color: theme.palette.warning.main },
        { name: 'Concluídas', value: dashboardData.purchaseStatistics.completedPurchases, color: theme.palette.success.main },
        { name: 'Canceladas', value: dashboardData.purchaseStatistics.cancelledPurchases, color: theme.palette.error.main }
    ];

    const contractStatusData = [
        { name: 'Ativos', value: dashboardData.contractStatistics.activeContracts, color: theme.palette.success.main },
        { name: 'Vencendo', value: dashboardData.contractStatistics.expiringSoonContracts, color: theme.palette.warning.main },
        { name: 'Vencidos', value: dashboardData.contractStatistics.expiredContracts, color: theme.palette.error.main }
    ];

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            return (
                <Paper sx={{ p: 1.5 }}>
                    <Typography variant="body2" fontWeight="bold">
                        {payload[0].name}
                    </Typography>
                    {payload.map((entry: any, index: number) => (
                        <Typography key={index} variant="body2" sx={{ color: entry.color }}>
                            {entry.dataKey === 'purchaseCount' ? 'Compras' : 'Contratos'}: {entry.value}
                        </Typography>
                    ))}
                </Paper>
            );
        }
        return null;
    };

    const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
        const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

        if (percent < 0.05) return null;

        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
                fontWeight="bold"
                fontSize="14"
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    return (
        <React.Fragment>
            <Grid item xs={12} md={12} lg={12}>
                <PageHeader
                    title="Dashboard"
                    subtitle="Visão geral de compras, licitações e contratos"
                    breadcrumbs={[
                        { label: 'Dashboard' }
                    ]}
                />
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
                <Paper
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Filtros
                        </Typography>
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Ano</InputLabel>
                            <Select
                                value={selectedYear}
                                label="Ano"
                                onChange={(e) => handleYearChange(Number(e.target.value))}
                            >
                                {years.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Paper>
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ height: '100%', cursor: 'pointer', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }} onClick={navigateToPurchases}>
                            <CardContent>
                                <Grid container spacing={3} sx={{ justifyContent: 'space-between' }}>
                                    <Grid item>
                                        <Typography color="textSecondary" gutterBottom variant="overline">
                                            COMPRAS E LICITAÇÕES
                                        </Typography>
                                        <Typography color="textPrimary" variant="h2">
                                            {dashboardData.purchaseStatistics.totalPurchases}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: getGrowthColor(dashboardData.purchaseStatistics.growthPercentage),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5
                                                }}
                                            >
                                                {getGrowthIcon(dashboardData.purchaseStatistics.growthPercentage)}
                                                {Math.abs(dashboardData.purchaseStatistics.growthPercentage).toFixed(1)}%
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                                                vs ano anterior
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item>
                                        <Avatar
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                height: 56,
                                                width: 56
                                            }}
                                        >
                                            <ShoppingCartIcon />
                                        </Avatar>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <HourglassEmptyIcon fontSize="small" color="warning" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Em Andamento</Typography>
                                                <Typography variant="h6">{dashboardData.purchaseStatistics.inProgressPurchases}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircleIcon fontSize="small" color="success" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Concluídas</Typography>
                                                <Typography variant="h6">{dashboardData.purchaseStatistics.completedPurchases}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CancelIcon fontSize="small" color="error" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Canceladas</Typography>
                                                <Typography variant="h6">{dashboardData.purchaseStatistics.cancelledPurchases}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary">Ano Atual</Typography>
                                            <Typography variant="h6">{dashboardData.purchaseStatistics.currentYearPurchases}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ height: '100%', cursor: 'pointer', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }} onClick={navigateToContracts}>
                            <CardContent>
                                <Grid container spacing={3} sx={{ justifyContent: 'space-between' }}>
                                    <Grid item>
                                        <Typography color="textSecondary" gutterBottom variant="overline">
                                            CONTRATOS
                                        </Typography>
                                        <Typography color="textPrimary" variant="h2">
                                            {dashboardData.contractStatistics.totalContracts}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: getGrowthColor(dashboardData.contractStatistics.growthPercentage),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5
                                                }}
                                            >
                                                {getGrowthIcon(dashboardData.contractStatistics.growthPercentage)}
                                                {Math.abs(dashboardData.contractStatistics.growthPercentage).toFixed(1)}%
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                                                vs ano anterior
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item>
                                        <Avatar
                                            sx={{
                                                backgroundColor: 'success.main',
                                                height: 56,
                                                width: 56
                                            }}
                                        >
                                            <DescriptionIcon />
                                        </Avatar>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircleIcon fontSize="small" color="success" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Ativos</Typography>
                                                <Typography variant="h6">{dashboardData.contractStatistics.activeContracts}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <WarningIcon fontSize="small" color="warning" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Vencendo</Typography>
                                                <Typography variant="h6">{dashboardData.contractStatistics.expiringSoonContracts}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CancelIcon fontSize="small" color="error" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Vencidos</Typography>
                                                <Typography variant="h6">{dashboardData.contractStatistics.expiredContracts}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary">Ano Atual</Typography>
                                            <Typography variant="h6">{dashboardData.contractStatistics.currentYearContracts}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Grid container spacing={3} sx={{ justifyContent: 'space-between' }}>
                                    <Grid item>
                                        <Typography color="textSecondary" gutterBottom variant="overline">
                                            ATAS DE REGISTRO DE PREÇO
                                        </Typography>
                                        <Typography color="textPrimary" variant="h2">
                                            {dashboardData.priceRegistrationStatistics.totalPriceRegistrations}
                                        </Typography>
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2" color="textSecondary">
                                                {dashboardData.priceRegistrationStatistics.currentYearPriceRegistrations} no ano atual
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item>
                                        <Avatar
                                            sx={{
                                                backgroundColor: 'info.main',
                                                height: 56,
                                                width: 56
                                            }}
                                        >
                                            <AssignmentIcon />
                                        </Avatar>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircleIcon fontSize="small" color="success" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Ativas</Typography>
                                                <Typography variant="h6">{dashboardData.priceRegistrationStatistics.activePriceRegistrations}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CancelIcon fontSize="small" color="error" />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">Vencidas</Typography>
                                                <Typography variant="h6">{dashboardData.priceRegistrationStatistics.expiredPriceRegistrations}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>

            {dashboardData.contractStatistics.expiringSoonContracts > 0 ? (
                <Grid item xs={12}>
                    <Alert severity="warning">
                        <Typography variant="body1">
                            Atenção: Existem {dashboardData.contractStatistics.expiringSoonContracts} contratos vencendo em breve.
                            <Button
                                color="inherit"
                                size="small"
                                onClick={navigateToContracts}
                                sx={{ ml: 2 }}
                            >
                                Ver Contratos
                            </Button>
                        </Typography>
                    </Alert>
                </Grid>
            ) : null}

            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Status das Compras
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {purchaseStatusData.some(d => d.value > 0) ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={purchaseStatusData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={CustomPieLabel}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {purchaseStatusData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            return (
                                                <Paper sx={{ p: 1.5 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {payload[0].name}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Quantidade: {payload[0].value}
                                                    </Typography>
                                                </Paper>
                                            );
                                        }
                                        return null;
                                    }} />
                                    <Legend
                                        verticalAlign="bottom"
                                        height={36}
                                        formatter={(value, entry: any) => `${value}: ${entry.payload.value}`}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 5 }}>
                                Nenhum dado disponível
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Status dos Contratos
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {contractStatusData.some(d => d.value > 0) ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={contractStatusData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={CustomPieLabel}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {contractStatusData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            return (
                                                <Paper sx={{ p: 1.5 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {payload[0].name}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Quantidade: {payload[0].value}
                                                    </Typography>
                                                </Paper>
                                            );
                                        }
                                        return null;
                                    }} />
                                    <Legend
                                        verticalAlign="bottom"
                                        height={36}
                                        formatter={(value, entry: any) => `${value}: ${entry.payload.value}`}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 5 }}>
                                Nenhum dado disponível
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Linha do Tempo - Compras e Contratos
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {dashboardData.timeline && dashboardData.timeline.length > 0 ? (
                            <ResponsiveContainer width="100%" height={350}>
                                <AreaChart
                                    data={dashboardData.timeline}
                                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                >
                                    <defs>
                                        <linearGradient id="colorPurchase" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8} />
                                            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="colorContract" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.8} />
                                            <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="period"
                                        style={{ fontSize: '12px' }}
                                    />
                                    <YAxis style={{ fontSize: '12px' }} />
                                    <Tooltip content={CustomTooltip} />
                                    <Legend />
                                    <Area
                                        type="monotone"
                                        dataKey="purchaseCount"
                                        stroke={theme.palette.primary.main}
                                        fillOpacity={1}
                                        fill="url(#colorPurchase)"
                                        name="Compras"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="contractCount"
                                        stroke={theme.palette.success.main}
                                        fillOpacity={1}
                                        fill="url(#colorContract)"
                                        name="Contratos"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 5 }}>
                                Nenhum dado disponível
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Distribuição por Modalidade
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {dashboardData.modalityDistribution && dashboardData.modalityDistribution.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={dashboardData.modalityDistribution}
                                    layout="vertical"
                                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" style={{ fontSize: '12px' }} />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        style={{ fontSize: '12px' }}
                                        width={90}
                                    />
                                    <Tooltip content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            return (
                                                <Paper sx={{ p: 1.5 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {payload[0].payload.name}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Quantidade: {payload[0].value}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Percentual: {payload[0].payload.percentage.toFixed(1)}%
                                                    </Typography>
                                                </Paper>
                                            );
                                        }
                                        return null;
                                    }} />
                                    <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                                        {dashboardData.modalityDistribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 5 }}>
                                Nenhum dado disponível
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Distribuição por Situação
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {dashboardData.situationDistribution && dashboardData.situationDistribution.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={dashboardData.situationDistribution}
                                    layout="vertical"
                                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" style={{ fontSize: '12px' }} />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        style={{ fontSize: '12px' }}
                                        width={90}
                                    />
                                    <Tooltip content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            return (
                                                <Paper sx={{ p: 1.5 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {payload[0].payload.name}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Quantidade: {payload[0].value}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Percentual: {payload[0].payload.percentage.toFixed(1)}%
                                                    </Typography>
                                                </Paper>
                                            );
                                        }
                                        return null;
                                    }} />
                                    <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                                        {dashboardData.situationDistribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 5 }}>
                                Nenhum dado disponível
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Distribuição por Tipo de Contrato
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {dashboardData.contractTypeDistribution && dashboardData.contractTypeDistribution.length > 0 ? (
                            <Grid container spacing={2}>
                                {dashboardData.contractTypeDistribution.map((item, index) => (
                                    <Grid item xs={12} sm={6} md={3} key={item.id}>
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                transition: 'all 0.3s',
                                                borderTop: `4px solid ${COLORS[index % COLORS.length]}`,
                                                '&:hover': { transform: 'scale(1.05)', boxShadow: 4 }
                                            }}
                                        >
                                            <Typography variant="h3" sx={{ color: COLORS[index % COLORS.length], fontWeight: 'bold' }}>
                                                {item.count}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 0.5 }}>
                                                {item.name}
                                            </Typography>
                                            <Chip
                                                label={`${item.percentage.toFixed(1)}%`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: COLORS[index % COLORS.length],
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 3 }}>
                                Nenhum dado disponível
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </React.Fragment>
    );
}
